import {
  ButtonHierarchy,
  ButtonSize,
  ButtonType,
  MainButton,
  Typography,
  TypographySize,
  TypographyWeight,
} from "@beydesign/storybook"
import { Box, useMediaQuery, useTheme } from "@mui/material"
import * as Sentry from "@sentry/react"
import { getAuth, getRedirectResult, GoogleAuthProvider } from "firebase/auth"
import React, { useEffect, useState } from "react"
import { useAppContext, useAppDispatchContext } from "../../app/Context"
import { useDeviceAV } from "../../app/hooks/useDeviceAV"
import { StorageKeys } from "../../constants"
import { ExcludedDomains, ExcludedEmails } from "../../constants/emails"
import { updateMixpanelUser } from "../../external/analytics"
import { signInWithGoogle } from "../../external/auth"
import { useStorage } from "../../hooks"
import { StateAction } from "../../types/State"
import { SnackbarType } from "../../types/UI"
import { User } from "../../types/User"
import { isValidEmail } from "../../utils/func"
import { ChatWithAgent } from "../CustomVideoConference/ChatWithAgent"
import { DeviceSelectionDialog } from "../MediaControlsBar/DeviceSelectionDialog"
import { MediaControlsBar } from "../MediaControlsBar/MediaControlsBar"
import { SnackbarWithText } from "../snackbar"
import { UserVideo } from "../UserVideo/UserVideo"

const isBlacklistedEmail = (email: string): boolean => {
  const lowerEmail = email.toLowerCase()

  if (
    ExcludedEmails.some((excluded) => excluded.toLowerCase() === lowerEmail)
  ) {
    return true
  }

  return ExcludedDomains.some((domain) =>
    lowerEmail.endsWith(`@${domain.toLowerCase()}`),
  )
}

const isExcludedFromHubspot = (
  email: string,
  hasCustomBranding: boolean,
): boolean => {
  if (hasCustomBranding) {
    return true
  }

  return isBlacklistedEmail(email)
}

interface UserLoginModalProps {
  open: boolean
  onClose: () => void
  isUserAnonymous: boolean
  onJoinCall?: () => void
  onResetFlow?: () => void
}

export const UserLoginModal: React.FC<UserLoginModalProps> = ({
  open,
  isUserAnonymous,
  onClose,
  onJoinCall,
  onResetFlow,
}) => {
  const [name, setName] = useState<string>("")
  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [email, setEmail] = useState<string>("")
  const [company, setCompany] = useState<string>("")
  const [errorMessage, setErrorMessage] = useState<string>("")

  const { setItem, getItem } = useStorage()

  const {
    audioInputs,
    videoInputs,
    isMicEnabled,
    isCameraEnabled,
    settingsOpen,
    selectedAudioInput,
    selectedVideoInput,
    permissionsGranted,
    handleAudioInputChange,
    handleVideoInputChange,
    handleSettingsClick,
    handleSettingsClose,
    toggleMicrophone,
    toggleCamera,
  } = useDeviceAV()

  const dispatch = useAppDispatchContext()
  const context = useAppContext()
  const auth = getAuth()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  useEffect(() => {
    const req = async () => {
      try {
        const result = await getRedirectResult(auth)
        if (result) {
          const credential = GoogleAuthProvider.credentialFromResult(result)
          const token = credential.accessToken
        }
      } catch (error) {
        setErrorMessage(error.code + " " + error.message)
        Sentry.captureException(error)
      }
    }
    req()
  }, [auth])

  useEffect(() => {
    if (!open || !isUserAnonymous) return

    const anonEmail = getItem<string | null>(StorageKeys.ANON_EMAIL)
    const anonName = getItem<string | null>(StorageKeys.ANON_NAME)
    const anonFirstName = getItem<string | null>(StorageKeys.ANON_FIRST_NAME)
    const anonLastName = getItem<string | null>(StorageKeys.ANON_LAST_NAME)
    const anonCompany = getItem<string | null>(StorageKeys.ANON_COMPANY)
    if (anonFirstName && anonLastName && anonEmail && isValidEmail(anonEmail)) {
      setFirstName(anonFirstName)
      setLastName(anonLastName)
      setName(`${anonFirstName} ${anonLastName}`)
      setEmail(anonEmail)
      if (anonCompany) {
        setCompany(anonCompany)
      }
    } else if (anonName && anonEmail && isValidEmail(anonEmail)) {
      setName(anonName)
      setEmail(anonEmail)
      if (anonCompany) {
        setCompany(anonCompany)
      }
    }
  }, [open, isUserAnonymous])

  const handleAnonLogin = async () => {
    try {
      if (firstName === "") {
        setErrorMessage("First name is required")
        return
      }
      if (lastName === "") {
        setErrorMessage("Last name is required")
        return
      }
      if (!isValidEmail(email)) {
        setErrorMessage("Invalid email")
        return
      }
      const fullName = `${firstName} ${lastName}`
      const user: User = {
        ...context.user,
        uid:
          context.user?.uid ||
          `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        email,
        name: fullName,
        firstName,
        lastName,
        company: company || undefined,
      }

      updateMixpanelUser(user)

      setItem(StorageKeys.ANON_EMAIL, email)
      setItem(StorageKeys.ANON_NAME, fullName)
      setItem(StorageKeys.ANON_FIRST_NAME, firstName)
      setItem(StorageKeys.ANON_LAST_NAME, lastName)
      if (company) {
        setItem(StorageKeys.ANON_COMPANY, company)
      }
      dispatch({ type: StateAction.setUser, payload: user })
      onJoinCall?.()
      // onClose()
    } catch (error) {
      setErrorMessage(error.message)
      Sentry.captureException(error)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithGoogle()
      if (!result.success) {
        setErrorMessage(result.error)
      } else {
        onClose()
      }
    } catch (error) {
      setErrorMessage(error.message)
      Sentry.captureException(error)
    }
  }

  const positionWeb = `var(--spacing-tokens-size-in-px-spacing-spacing-xl)`
  const positionMobile = `var(--spacing-tokens-size-in-px-spacing-spacing-9xl)`

  if (!open) return null

  return (
    <Box
      position="absolute"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      width="100%"
      height="100%"
      top={0}
      left={0}
      zIndex={10}
      sx={{
        backdropFilter: "blur(10px)",
        backgroundColor: "rgba(0, 0, 0, 0.15)",
        boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
        border: "1px solid rgba(255, 255, 255, 0.18)",
        borderRadius: "1rem",
      }}
      onClick={onClose}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        position="relative"
        width="100%"
        height="100%"
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          gap={"var(--spacing-tokens-size-in-px-spacing-spacing-3xl)"}
          width={"80%"}
          maxWidth={"600px"}
        >
          <SnackbarWithText
            open={errorMessage !== ""}
            severity={SnackbarType.error}
            text={errorMessage}
            handleClose={() => setErrorMessage("")}
          />

          {isUserAnonymous && (
            <Box
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
              gap={"var(--spacing-tokens-size-in-px-spacing-spacing-2xl)"}
              width={"100%"}
            >
              <Box
                display={"flex"}
                flexDirection={"column"}
                gap={"var(--spacing-tokens-size-in-px-spacing-spacing-xs)"}
                alignItems={"center"}
                marginBottom={
                  "var(--spacing-tokens-size-in-px-spacing-spacing-xl)"
                }
              >
                <Typography
                  text="Enter your details"
                  size={TypographySize.HeadingL}
                  weight={TypographyWeight.SemiBold}
                  color="var(--colors-light-text-text-white)"
                  textStyle={{ textAlign: "center" }}
                />
                <Typography
                  text="Hit 'Start Conversation' to begin!"
                  size={TypographySize.HeadingM}
                  weight={TypographyWeight.Regular}
                  color="var(--colors-light-text-text-white)"
                  textStyle={{ textAlign: "center" }}
                />
              </Box>
              <form
                name="contactForm"
                onSubmit={(e) => {
                  e.preventDefault()
                  handleAnonLogin()
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={"var(--spacing-tokens-size-in-px-spacing-spacing-lg)"}
                  width={isMobile ? "100%" : "400px"}
                >
                  <Box
                    display="flex"
                    gap="var(--spacing-tokens-size-in-px-spacing-spacing-md)"
                    width="100%"
                  >
                    <input
                      type="text"
                      name="firstName"
                      placeholder="First Name*"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        fontSize: "16px",
                        outline: "none",
                        backdropFilter: "blur(10px)",
                      }}
                    />
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Last Name*"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        fontSize: "16px",
                        outline: "none",
                        backdropFilter: "blur(10px)",
                      }}
                    />
                  </Box>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address*"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      fontSize: "16px",
                      outline: "none",
                      backdropFilter: "blur(10px)",
                    }}
                  />
                  <input
                    type="text"
                    name="company"
                    placeholder="Company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      fontSize: "16px",
                      outline: "none",
                      backdropFilter: "blur(10px)",
                    }}
                  />
                  {!isExcludedFromHubspot(
                    email,
                    context.agent?.custom_branding,
                  ) && (
                    <input
                      type="text"
                      className="hidden"
                      name="Source"
                      value="Demo"
                      readOnly
                    />
                  )}
                  <Box
                    marginTop={
                      "var(--spacing-tokens-size-in-px-spacing-spacing-xl)"
                    }
                    display="flex"
                    justifyContent="center"
                    width="100%"
                  >
                    <MainButton
                      type={
                        isExcludedFromHubspot(
                          email,
                          context.agent?.custom_branding,
                        )
                          ? ButtonType.Button
                          : ButtonType.Submit
                      }
                      onClick={
                        isExcludedFromHubspot(
                          email,
                          context.agent?.custom_branding,
                        )
                          ? handleAnonLogin
                          : undefined
                      }
                      hierarchy={ButtonHierarchy.PrimaryLemon}
                      size={ButtonSize.lg}
                      text="Start Conversation"
                      data-hs-ignore="true"
                    />
                  </Box>
                </Box>
              </form>
            </Box>
          )}
          {!isUserAnonymous && (
            <React.Fragment>
              <Typography
                text="Login with Google to continue"
                size={TypographySize.HeadingL}
                weight={TypographyWeight.SemiBold}
                color="var(--colors-light-text-text-white)"
                textStyle={{ textAlign: "center" }}
              />
              <MainButton
                onClick={handleGoogleLogin}
                hierarchy={ButtonHierarchy.PrimaryLemon}
                size={ButtonSize.xl}
                text="Login"
                leadingIcon="GoogleLogo"
                showLeadingIcon
              />
            </React.Fragment>
          )}
        </Box>
        {isUserAnonymous && (
          <React.Fragment>
            <Box
              position="absolute"
              bottom={isMobile ? positionMobile : positionWeb}
              left={!isMobile && positionWeb}
              right={isMobile ? "5%" : undefined}
              width="25%"
              height="auto"
              borderRadius={
                "var(--spacing-tokens-size-in-px-spacing-spacing-xl)"
              }
            >
              <UserVideo />
            </Box>

            <MediaControlsBar
              isMicEnabled={isMicEnabled}
              isCameraEnabled={isCameraEnabled}
              toggleMicrophone={toggleMicrophone}
              toggleCamera={toggleCamera}
              onSettingsClick={handleSettingsClick}
              bottom={isMobile ? "2%" : positionWeb}
            />
          </React.Fragment>
        )}
      </Box>
      {isUserAnonymous && (
        <React.Fragment>
          <DeviceSelectionDialog
            open={settingsOpen}
            onClose={handleSettingsClose}
            audioInputs={audioInputs}
            videoInputs={videoInputs}
            selectedAudioInput={selectedAudioInput}
            selectedVideoInput={selectedVideoInput}
            handleAudioInputChange={handleAudioInputChange}
            handleVideoInputChange={handleVideoInputChange}
          />
          <ChatWithAgent
            agent={context.agent}
            sx={{ top: "1rem", left: "1rem" }}
            onBackButtonClick={onResetFlow}
          />
        </React.Fragment>
      )}
    </Box>
  )
}
